import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Tag, Calendar, Clock } from 'lucide-react';
import { INITIAL_BLOGS } from '@/data/blogsData';

const LOCAL_STORAGE_BLOGS = 'stellars_custom_blogs';

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    let list = INITIAL_BLOGS;
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_BLOGS);
      if (stored) {
        list = JSON.parse(stored);
      }
    } catch (e) {
      console.error(e);
    }
    setBlogs(list);
  }, []);

  return (
    <section id="blogs" className="py-20 bg-[#141A29] text-white border-b border-white/10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-12">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/30 text-[#F3453F] text-xs font-bold uppercase tracking-wider">
              <BookOpen size={14} />
              <span>Thought Leadership & Insights</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading leading-tight">
              Latest Playbooks, AI Marketing & Growth Insights
            </h2>
            <p className="text-base text-white/70 font-sans">
              Authoritative guides on high-ticket real estate lead generation, WhatsApp AI automation, and search engine dominance.
            </p>
          </div>

          <div>
            <Link
              to="/blogs"
              className="px-6 py-3 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-xs sm:text-sm transition-all shadow-lg shadow-[#F3453F]/20 inline-flex items-center gap-2"
            >
              <span>Explore All Blogs & Playbooks</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Blog Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogs.slice(0, 3).map((blog) => (
            <div 
              key={blog.id} 
              className="glass-panel bg-[#202942] rounded-2xl overflow-hidden border border-white/15 hover:border-[#F3453F]/60 transition-all duration-300 flex flex-col justify-between group shadow-xl"
            >
              <div className="space-y-4">
                {/* Thumbnail Image */}
                <div className="relative h-48 overflow-hidden bg-[#181F32]">
                  <img 
                    src={blog.imageUrl} 
                    alt={blog.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80";
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-[#181F32]/95 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-[#F3453F] border border-[#F3453F]/40 uppercase tracking-wider">
                    {blog.topic}
                  </div>
                </div>

                {/* Details */}
                <div className="px-6 space-y-3">
                  <div className="flex items-center gap-4 text-xs text-white/70 font-sans">
                    <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#F3453F]" /> {blog.date}</span>
                    <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#F3453F]" /> {blog.readTime}</span>
                  </div>

                  <h3 className="text-lg font-bold text-white font-heading leading-snug group-hover:text-[#F3453F] transition-colors line-clamp-2">
                    {blog.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-white/80 font-sans leading-relaxed line-clamp-3">
                    {blog.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {blog.keywords && blog.keywords.slice(0, 3).map((kw, kIdx) => (
                      <span key={kIdx} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/10 text-[11px] font-medium text-white/85 border border-white/15">
                        <Tag size={10} className="text-[#F3453F]" />
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="p-6 pt-4 border-t border-white/10 mt-4 flex items-center justify-between">
                <span className="text-xs font-semibold text-white/70">By {blog.author}</span>
                <Link
                  to={`/blog/${blog.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F3453F] hover:text-white hover:bg-[#F3453F] px-3 py-1.5 rounded-lg bg-[#F3453F]/10 border border-[#F3453F]/30 transition-all"
                >
                  <span>Read Article</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default BlogSection;
